import { theme, useFallbackStorageValue, useResponsive } from "@product/scmp-sdk";
import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import { fetchQuery, graphql, readInlineData, useRelayEnvironment } from "react-relay";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { currentArticleAtom } from "~/lib/current-item";
import { useFlag } from "~/lib/feature-flag";
import type { hooksUseNotificationQueueQuery } from "~/queries/__generated__/hooksUseNotificationQueueQuery.graphql";
import type { hooksUseNotificationReadStatusArticle$key } from "~/queries/__generated__/hooksUseNotificationReadStatusArticle.graphql";
import type { hooksUseNotificationsSection$key } from "~/queries/__generated__/hooksUseNotificationsSection.graphql";

import { currentNotificationSectionAtom } from "./atoms";

import type { Type } from ".";
import { Notification } from ".";

// Breaking news queue: https://staging-scmp-d8.product-web.dev-2.scmp.tech/admin/structure/entityqueue/notification/notification
// Section notification queue: https://staging-scmp-d8.product-web.dev-2.scmp.tech/scmp-entity-subqueue/notification_top
export const useNotifications = () => {
  const currentSection = useAtomValue(currentNotificationSectionAtom);
  const currentSectionData = useMemo(() => {
    if (!currentSection) return;

    const data = readInlineData<hooksUseNotificationsSection$key>(
      graphql`
        fragment hooksUseNotificationsSection on Section @inline {
          ...notificationSection
          entityId
        }
      `,
      currentSection,
    );
    return data;
  }, [currentSection]);

  const currentSectionNotification = useNotificationItem(
    currentSectionData ? `notification_top_${currentSectionData.entityId}` : "",
  );

  const breakingNewsNotification = useNotificationItem("notification");
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const isTickerWidgetEnabled = useFlag("enable_ticker_widget");
  return {
    breakingNewsNotification: isDesktopUp && breakingNewsNotification && isTickerWidgetEnabled && (
      <Notification
        reference={breakingNewsNotification.item}
        variant={{ type: "breaking" }}
        onClose={() => {
          track(breakingNewsNotification.item.entityId, "breaking", "close");
          breakingNewsNotification.markRead();
        }}
        onImpression={() => {
          track(breakingNewsNotification.item.entityId, "breaking", "impression");
        }}
        onRead={() => {
          track(breakingNewsNotification.item.entityId, "breaking", "read");
          breakingNewsNotification.markRead();
        }}
      />
    ),
    currentSectionNotification: isDesktopUp &&
      currentSectionNotification &&
      currentSectionData &&
      isTickerWidgetEnabled && (
        <Notification
          reference={currentSectionNotification.item}
          variant={{ reference: currentSectionData, type: "section" }}
          onClose={() => {
            track(currentSectionNotification.item.entityId, "section", "close");
            currentSectionNotification.markRead();
          }}
          onImpression={() => {
            track(currentSectionNotification.item.entityId, "section", "impression");
          }}
          onRead={() => {
            track(currentSectionNotification.item.entityId, "section", "read");
            currentSectionNotification.markRead();
          }}
        />
      ),
  };
};

const useNotificationItem = (queueName: string) => {
  const item = useFetchNotificationItem(queueName);
  const { hasRead, markRead } = useNotificationReadStatus(queueName, item?.entityId ?? "");
  if (!item) return;
  if (hasRead) return;

  return { item: { ...item, entityId: item.entityId }, markRead };
};

const useFetchNotificationItem = (queueName: string) => {
  const environment = useRelayEnvironment();
  const router = useRouter();

  const [{ result }, { execute }] = useAsync(async () => {
    if (!queueName) return;
    const response = await fetchQuery<hooksUseNotificationQueueQuery>(
      environment,
      graphql`
        query hooksUseNotificationQueueQuery($queueName: String!) {
          queue(filter: { name: $queueName }) {
            items(first: 1) {
              edges {
                node {
                  ... on Article {
                    __typename
                    entityId
                  }
                  ...notificationArticle
                }
              }
            }
          }
        }
      `,
      {
        queueName,
      },
    ).toPromise();

    return response;
  });

  // Fetch it once again when route change
  useEffect(() => {
    void execute();
  }, [execute, router.asPath]);

  const item = result?.queue?.items?.edges[0]?.node;

  if (item?.__typename !== "Article") return;
  if (item.entityId === undefined) return;

  return { ...item, entityId: item.entityId };
};

const useNotificationReadStatus = (
  queueName: string,
  notificationItemEntityId: string | undefined,
) => {
  const currentArticle = useAtomValue(currentArticleAtom);
  const currentArticleId = useMemo(() => {
    if (!currentArticle) return;

    const data = readInlineData<hooksUseNotificationReadStatusArticle$key>(
      graphql`
        fragment hooksUseNotificationReadStatusArticle on Article @inline {
          entityId
        }
      `,
      currentArticle,
    );

    return data.entityId;
  }, [currentArticle]);

  // Keep key name as `ticker_${queueName}` for backward compatibility with v1
  const { set: setLastReadNotificationItemEntityId, value: lastReadNotificationItemEntityId } =
    useFallbackStorageValue<string>(`ticker_${queueName}`, {
      parse: value => value,
      stringify: value => value,
    });

  const markRead = useCallback(() => {
    if (!queueName) return;
    if (!notificationItemEntityId) return;
    setLastReadNotificationItemEntityId(notificationItemEntityId);
  }, [notificationItemEntityId, queueName, setLastReadNotificationItemEntityId]);

  useEffect(() => {
    if (!queueName) return;
    if (currentArticleId === undefined) return;
    if (notificationItemEntityId !== currentArticleId) return;
    setLastReadNotificationItemEntityId(currentArticleId);
  }, [currentArticleId, notificationItemEntityId, queueName, setLastReadNotificationItemEntityId]);

  const hasLastRead =
    lastReadNotificationItemEntityId !== undefined &&
    notificationItemEntityId === lastReadNotificationItemEntityId;
  const isReading = currentArticleId !== undefined && currentArticleId === notificationItemEntityId;
  const hasRead = hasLastRead || isReading;

  return { hasRead, markRead };
};

function track(entityId: string, type: Type, actionType: "read" | "close" | "impression") {
  const computeAction = () => {
    const computeType = (type: Type) => (type === "breaking" ? "breaking" : "others");
    switch (actionType) {
      case "read":
        return `Click to Open/${computeType(type)}` as const;
      case "close":
        return `Close/${computeType(type)}` as const;
      case "impression":
        return `Impression/${computeType(type)}` as const;
    }
  };
  const action = computeAction();
  sendGATracking({
    action,
    category: "Ticker",
    label: entityId,
  });
}
